/**
 * Nowruz egg designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nowruz`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nowruzEggDesignsPack: ToilePack = {
    slug: 'nowruz-egg-designs',
    category: 'season',
    name: 'Nowruz egg designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'tile', label: 'Tile pattern', subject: 'three eggs painted in turquoise and cobalt tile patterns, eight-pointed stars and interlacing knots like the tilework of Isfahan'},
        {key: 'paisley', label: 'Paisley', subject: 'three eggs covered in paisley boteh shapes with small flowers and dots, in deep red, saffron and green'},
        {key: 'floral', label: 'Floral', subject: 'three eggs painted with roses, tulips and climbing vines, a small bird on one, on cream and pale green'},
    ],
    styles: [
        {key: 'vector', label: 'Flat vector', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp pattern edges', palette: 'turquoise, cobalt, saffron and cream on each egg', framing: 'the eggs centered in a row on a pale cream ground'},
        {key: 'gouache', label: 'Gouache', style: 'hand-painted gouache on paper, visible brushstrokes, slightly uneven dots and hard edges', palette: 'warm reds, deep green and gold on each egg, matte paint', framing: 'the eggs centered, resting on a plain warm-white sheet'},
        {key: 'marker', label: 'Felt-tip marker', style: 'felt-tip marker drawing as a child would copy it, thick simple lines and bold dotted fills', palette: 'six bright marker colors on white eggs', framing: 'the eggs centered on a plain white paper background'},
    ],
    directions: ['gold only', 'one big egg', "kids' version"],
    directionHint: 'try: gold only',
    seo: {
        title: 'Nowruz Egg Designs: Painted Egg Patterns to Copy',
        metaDescription: 'Nowruz egg design ideas: tile, paisley and floral patterns on painted eggs, shown as flat vector, gouache and felt-tip marker.',
        h1: 'Nowruz egg designs: tile, paisley, floral',
        tagline: 'Nine egg sheets, three patterns, three ways of painting each.',
        intro: "Egg designs for the eggs set on the haft-sin table, for a family or class to copy with paint or markers. Each sheet shows three eggs in one pattern: tile work with stars and knots, paisley with boteh shapes, or roses and tulips with vines. The three finishes show each pattern as flat vector, gouache paint and a child's felt-tip marker. Type a single colour or a different egg count and the pattern is drawn to match.",
        useCases: [
            'a painting session before Nowruz',
            'a reference sheet beside the haft-sin table',
            'a school craft lesson',
            'a family egg-painting afternoon',
        ],
        faq: [
            {q: 'Are these photographs of real eggs?', a: 'No. They are illustrations of decorated eggs, made to be copied by hand.'},
            {q: 'How many eggs are on a sheet?', a: 'Three in each pattern. Each picture shows a few eggs at a time, so you can copy them one by one.'},
            {q: 'Which finish is easiest for a child to copy?', a: 'The felt-tip marker finish, drawn with thick simple lines and bold dotted fills.'},
        ],
    },
}
